<template>
  <div class="compliance-consult-page">
    <!-- Dashboard-style header strip (mirrors ComplianceHealthDashboard
         so the "Ask the Agent" page feels like a focused mode of the
         same product, not a different surface). -->
    <div class="dashboard-header">
      <div class="header-content">
        <div class="header-title">
          <h1><i class="fas fa-shield-halved"></i> Compliance Consult</h1>
          <p class="header-subtitle">
            <span v-if="selectedProject">Asking about <strong>{{ selectedProject.name }}</strong></span>
            <span v-else-if="!projectOptions.length">No projects found — add one to scope your consult</span>
            <span v-else>Asking across your portfolio — pick a project to scope</span>
          </p>
        </div>
        <div class="header-actions">
          <select
            v-model.number="selectedProjectIdModel"
            class="project-select"
            @change="onProjectChange"
          >
            <option :value="0">All Projects</option>
            <option v-for="p in projectOptions" :key="p.id" :value="p.id">
              {{ p.name }}
            </option>
          </select>
          <router-link
            to="/setup/compliance-client"
            class="btn btn-success"
            title="Onboard a new compliance client"
          >
            <i class="fas fa-plus-circle"></i>
            New Client
          </router-link>
          <button
            class="btn btn-back"
            @click="goBack"
            title="Back to the Compliance Health dashboard"
          >
            <i class="fas fa-arrow-left"></i>
            Dashboard
          </button>
          <button
            class="btn-refresh"
            :disabled="loadingSummary"
            @click="refreshAll"
            title="Refresh project list and live KPIs"
          >
            <i :class="loadingSummary ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"></i>
            Refresh
          </button>
        </div>
      </div>
    </div>

    <!-- Compact KPI Bar -->
    <div class="kpi-bar">
      <div class="kpi-item score-kpi" :class="getScoreClass(summary.average_score)">
        <div class="kpi-value">{{ summary.average_score || 0 }}%</div>
        <div class="kpi-label">Avg Score</div>
      </div>
      <div class="kpi-divider"></div>
      <div class="kpi-item">
        <div class="kpi-icon excellent"><i class="fas fa-check-circle"></i></div>
        <div class="kpi-data">
          <div class="kpi-value">{{ (summary.status_breakdown?.excellent || 0) + (summary.status_breakdown?.good || 0) }}</div>
          <div class="kpi-label">Compliant</div>
        </div>
      </div>
      <div class="kpi-item">
        <div class="kpi-icon warning"><i class="fas fa-exclamation-triangle"></i></div>
        <div class="kpi-data">
          <div class="kpi-value">{{ summary.status_breakdown?.needs_attention || 0 }}</div>
          <div class="kpi-label">Attention</div>
        </div>
      </div>
      <div class="kpi-item">
        <div class="kpi-icon critical"><i class="fas fa-times-circle"></i></div>
        <div class="kpi-data">
          <div class="kpi-value">{{ (summary.status_breakdown?.at_risk || 0) + (summary.status_breakdown?.critical || 0) }}</div>
          <div class="kpi-label">At Risk</div>
        </div>
      </div>
      <div class="kpi-divider"></div>
      <div class="kpi-item">
        <div class="kpi-icon total"><i class="fas fa-building"></i></div>
        <div class="kpi-data">
          <div class="kpi-value">{{ summary.total_entities || 0 }}</div>
          <div class="kpi-label">Total Entities</div>
        </div>
      </div>
      <div class="kpi-item">
        <div class="kpi-icon with-data"><i class="fas fa-check-square"></i></div>
        <div class="kpi-data">
          <div class="kpi-value">{{ summary.entities_with_data || 0 }}</div>
          <div class="kpi-label">With Data</div>
        </div>
      </div>
      <div class="kpi-item">
        <div class="kpi-icon no-data"><i class="fas fa-question-circle"></i></div>
        <div class="kpi-data">
          <div class="kpi-value">{{ summary.entities_without_data || summary.status_breakdown?.no_data || 0 }}</div>
          <div class="kpi-label">No Data</div>
        </div>
      </div>
    </div>

    <p class="page-sub">
      Ask about this portfolio. Answers use certificates on file, health
      snapshots, check requirements, gap notifications, and earlier
      conversations — not outside sources.
    </p>

    <div class="consult-shell">
      <div class="consult-chat-pane">
        <ComplianceConsultChat
          :project-id="selectedProjectId"
          :summary="summary"
          :is-sidebar="false"
          initial-depth="quick"
          :initial-query="String(route.query.q || '')"
          @notify="onNotify"
          @show-entity-details="onShowEntityDetails"
          @show-document="onShowDocument"
        />
      </div>
    </div>

    <!-- Outreach is an intentional task, so it opens only when requested
         rather than occupying half of every chat session. -->
    <ComplianceOutreachDrawer
      :payload="outreachPayload"
      :project-id="selectedProjectId"
      :scope-label="selectedProject ? `${selectedProject.name} records` : 'portfolio records'"
      @close="outreachPayload = null"
      @show-entity-details="onShowEntityDetails"
    />

    <!-- Entity Documents modal — opened when an evidence card or action
         points at an entity instead of a raw document UUID. Mirrors the
         pattern used on ComplianceHealthDashboard. -->
    <EntityDocumentsModal
      :show="showDocsModal"
      :subject-id="docsModalSubjectId"
      :entity-name="docsModalEntityName"
      @close="showDocsModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import ComplianceConsultChat from '@/components/compliance/ComplianceConsultChat.vue'
import ComplianceOutreachDrawer from '@/components/compliance/ComplianceOutreachDrawer.vue'
import EntityDocumentsModal from '@/components/compliance/EntityDocumentsModal.vue'

export default {
  name: 'ComplianceConsult',
  components: { ComplianceConsultChat, ComplianceOutreachDrawer, EntityDocumentsModal },
  setup() {
    const router = useRouter()
    const route = useRoute()

    // Real `null` is what the chat component expects (portfolio-wide).
    // The <select> binding can't round-trip null cleanly across <option>
    // values, so we use 0 as the "All Projects" sentinel in the model
    // and convert at the boundary.
    const selectedProjectId = ref(
      route.query.project_id ? Number(route.query.project_id) : null
    )
    const selectedProjectIdModel = computed({
      get: () => selectedProjectId.value || 0,
      set: (v) => {
        const n = Number(v)
        selectedProjectId.value = n > 0 ? n : null
      },
    })

    const projectOptions = ref([])
    const summary = ref({})
    const loadingSummary = ref(false)
    const outreachPayload = ref(null)

    const selectedProject = computed(() => {
      if (!selectedProjectId.value) return null
      return projectOptions.value.find(p => p.id === selectedProjectId.value) || null
    })
    // ── Loaders ──────────────────────────────────────────────────────
    const loadProjects = async () => {
      try {
        const res = await api.get('/projects/')
        const data = res.data || {}
        // /projects/ returns { projects: [...] } in this app; fall back
        // to the other plausible shapes so we never end up with a hidden
        // dropdown when one of those changes upstream.
        const arr = data.projects || data.results || (Array.isArray(data) ? data : [])
        projectOptions.value = arr.map(p => ({ id: p.id, name: p.name }))
      } catch (e) {
        console.warn('failed to load projects', e)
        projectOptions.value = []
      }
    }

    const loadSummary = async () => {
      loadingSummary.value = true
      try {
        const params = selectedProjectId.value
          ? `?project_id=${selectedProjectId.value}`
          : ''
        const res = await api.get(`/compliance-health/summary/${params}`)
        summary.value = res.data || {}
      } catch (e) {
        console.warn('failed to load compliance summary', e)
        summary.value = {}
      } finally {
        loadingSummary.value = false
      }
    }

    const refreshAll = async () => {
      await Promise.all([loadProjects(), loadSummary()])
    }

    // ── Navigation / events ──────────────────────────────────────────
    const onProjectChange = () => {
      // Keep the URL in sync so reload preserves selection and the
      // chat sees the new project_id prop.
      const query = { ...route.query }
      if (selectedProjectId.value) {
        query.project_id = String(selectedProjectId.value)
      } else {
        delete query.project_id
      }
      router.replace({ path: '/compliance-health/consult', query })
      outreachPayload.value = null
      loadSummary()
    }

    const goBack = () => {
      const to = selectedProjectId.value
        ? `/compliance-health?project_id=${selectedProjectId.value}`
        : '/compliance-health'
      router.push(to)
    }

    const onNotify = (payload) => {
      outreachPayload.value = payload
    }

    const onShowEntityDetails = async (subjectId) => {
      let recordProjectId = selectedProjectId.value
      if (!recordProjectId) {
        try {
          const response = await api.get(`/compliance-health/entities/${subjectId}/`)
          recordProjectId = response.data?.project_id || null
        } catch (error) {
          if (window.$toast) {
            window.$toast.error(
              `Could not open this compliance record: ${error.response?.data?.error || error.message}`
            )
          }
          return
        }
      }

      if (recordProjectId) {
        router.push({
          name: 'EntityRecord',
          params: {
            projectId: recordProjectId,
            subjectId,
          },
        })
      } else if (window.$toast) {
        window.$toast.info('Choose a project before opening a compliance record.')
      }
    }

    // ── Document deep-link (modal-first, never /documents/:id 404) ──
    const showDocsModal = ref(false)
    const docsModalSubjectId = ref(null)
    const docsModalEntityName = ref('')

    const onShowDocument = (payload) => {
      const subjectId = payload?.subject_id || payload?.subjectId
      if (subjectId) {
        docsModalSubjectId.value = subjectId
        docsModalEntityName.value = payload?.entity_name || payload?.entityName || ''
        showDocsModal.value = true
        return
      }
      const docId = payload?.document_id || payload?.documentId
      if (docId && selectedProjectId.value) {
        router.push(`/projects/${selectedProjectId.value}/documents?document_id=${docId}`)
        return
      }
      if (window.$toast) {
        window.$toast.info('Open the entity to view this document.')
      }
    }

    // KPI score-kpi class (matches the dashboard's getScoreClass).
    const getScoreClass = (score) => {
      const s = Number(score) || 0
      if (s >= 90) return 'excellent'
      if (s >= 75) return 'good'
      if (s >= 60) return 'warning'
      if (s >= 40) return 'at-risk'
      return 'critical'
    }

    // Reload summary if user pastes a new ?project_id= into the URL.
    watch(
      () => route.query.project_id,
      (val) => {
        const n = val ? Number(val) : null
        if (n !== selectedProjectId.value) {
          selectedProjectId.value = n
          loadSummary()
        }
      }
    )

    onMounted(() => { refreshAll() })

    return {
      route,
      selectedProjectId,
      selectedProjectIdModel,
      projectOptions,
      selectedProject,
      summary,
      loadingSummary,
      onProjectChange,
      refreshAll,
      goBack,
      outreachPayload,
      onNotify,
      onShowEntityDetails,
      onShowDocument,
      showDocsModal,
      docsModalSubjectId,
      docsModalEntityName,
      getScoreClass,
    }
  },
}
</script>

<style scoped>
.compliance-consult-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  padding: 12px 16px;
  background: var(--color-bg, #f9fafb);
}

/* ── Dashboard-style header strip ─────────────────────────────────── */
.dashboard-header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  padding: 0.75rem 1.25rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}
.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.header-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.header-title h1 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.header-title h1 i {
  color: #3b82f6;
  font-size: 1rem;
}
.header-subtitle {
  color: #64748b;
  margin: 0;
  font-size: 0.8rem;
  margin-left: 0.5rem;
  padding-left: 0.5rem;
  border-left: 1px solid #e2e8f0;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.project-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.85rem;
  background: white;
  min-width: 220px;
  cursor: pointer;
  font-weight: 500;
  color: #1e293b;
}
.project-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

/* Action buttons mirror the dashboard's btn classes so they look
   identical without importing the dashboard stylesheet. */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  border: 1px solid transparent;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s;
}
.btn-success {
  background: #10b981;
  color: #fff;
  border-color: #10b981;
}
.btn-success:hover { background: #059669; }
.btn-back {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-back:hover { background: #f8fafc; }
.btn-refresh {
  background: #3b82f6;
  color: #fff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  transition: all 0.15s;
}
.btn-refresh:hover:not(:disabled) { background: #2563eb; }
.btn-refresh:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── KPI bar (cloned from ComplianceHealthDashboard) ──────────────── */
.kpi-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  padding: 0.75rem 1.25rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  flex-wrap: nowrap;
  overflow-x: auto;
}
.kpi-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  flex-shrink: 0;
}
.kpi-item.score-kpi {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  background: #f8fafc;
}
.kpi-item.score-kpi.excellent { background: #d1fae5; }
.kpi-item.score-kpi.good { background: #dbeafe; }
.kpi-item.score-kpi.warning { background: #fef3c7; }
.kpi-item.score-kpi.at-risk { background: #ffedd5; }
.kpi-item.score-kpi.critical { background: #fee2e2; }

.kpi-divider {
  width: 1px;
  height: 32px;
  background: #e2e8f0;
  flex-shrink: 0;
}
.kpi-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  flex-shrink: 0;
}
.kpi-icon.excellent { background: #d1fae5; color: #059669; }
.kpi-icon.warning { background: #fef3c7; color: #d97706; }
.kpi-icon.critical { background: #fee2e2; color: #dc2626; }
.kpi-icon.total { background: #e0e7ff; color: #4f46e5; }
.kpi-icon.with-data { background: #dbeafe; color: #2563eb; }
.kpi-icon.no-data { background: #f1f5f9; color: #94a3b8; }
.kpi-data { display: flex; flex-direction: column; }
.kpi-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.1;
}
.kpi-item.score-kpi .kpi-value { font-size: 1.5rem; }
.kpi-item.score-kpi.excellent .kpi-value { color: #059669; }
.kpi-item.score-kpi.good .kpi-value { color: #3b82f6; }
.kpi-item.score-kpi.warning .kpi-value { color: #d97706; }
.kpi-item.score-kpi.at-risk .kpi-value { color: #f97316; }
.kpi-item.score-kpi.critical .kpi-value { color: #dc2626; }
.kpi-label {
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

/* ── Chat shell ───────────────────────────────────────────────────── */
.page-sub {
  margin: 0 0 8px;
  font-size: 0.8rem;
  color: var(--color-text-muted, #6b7280);
  max-width: 900px;
}
.consult-shell {
  flex: 1;
  min-height: 0;
  width: 100%;
  margin: 0;
}
.consult-chat-pane {
  min-width: 0;
  min-height: 0;
  height: 100%;
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* The pane now spans the viewport so result tables get room, but prose
   still needs a readable line length. Cap the answer text, not the
   bubble, so inline result previews keep the extra width. */
.consult-chat-pane :deep(.message-content) {
  max-width: 95ch;
}

.workspace-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #e2e8f0;
}
.workspace-header h2 {
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
}
.workspace-tabs {
  display: flex;
  gap: 4px;
}
.workspace-tabs button {
  padding: 6px 9px;
  border: 1px solid transparent;
  border-radius: 7px;
  background: transparent;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}
.workspace-tabs button.active {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
}
.workspace-tabs button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.workspace-tabs button span {
  margin-left: 3px;
}
.workspace-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 14px;
}
.result-list,
.recipient-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.result-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #94a3b8;
  border-radius: 9px;
}
.result-card.status-critical { border-left-color: #dc2626; }
.result-card.status-warning { border-left-color: #d97706; }
.result-card.status-good { border-left-color: #059669; }
.result-title-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.result-title-row h3 {
  margin: 0;
  color: #0f172a;
  font-size: 0.9rem;
}
.result-title-row strong {
  color: #334155;
  font-size: 0.85rem;
}
.result-copy p {
  margin: 2px 0 6px;
  color: #64748b;
  font-size: 0.72rem;
  text-transform: capitalize;
}
.result-copy ul {
  margin: 0;
  padding-left: 17px;
  color: #475569;
  font-size: 0.75rem;
}
.record-link {
  align-self: start;
  border: 0;
  background: transparent;
  color: #2563eb;
  cursor: pointer;
}
.workspace-empty {
  display: grid;
  place-items: center;
  align-content: center;
  height: 100%;
  padding: 24px;
  color: #64748b;
  text-align: center;
}
.workspace-empty i {
  margin-bottom: 10px;
  color: #94a3b8;
  font-size: 1.75rem;
}
.workspace-empty h3 {
  margin: 0 0 5px;
  color: #334155;
  font-size: 0.95rem;
}
.workspace-empty p {
  max-width: 360px;
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.5;
}
@media (max-width: 1120px) {
  .compliance-consult-page {
    height: auto;
    min-height: calc(100vh - 60px);
  }
  .consult-chat-pane {
    min-height: 620px;
  }
}

@media (max-width: 640px) {
  .compliance-consult-page {
    padding: 8px;
  }
}
</style>
